import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import agent from "../../api/agent";
import {
  ApiResponse,
  ClientDownloadResponse,
  ClientUIResponse,
  ClientUISingleResponse,
} from "../../models/ApiResponse";
import {
  AddClientRequest,
  UpdateClientRequest,
} from "../../models/requestModels/clientRequest";
import { Client } from "../../models/responseModels/clientsResponse";

type InitialState = {
  isClientLoading: boolean;
  clientList: Client[];
  errorMessage: string | null;
};

const initialState: InitialState = {
  isClientLoading: false,
  clientList: [],
  errorMessage: null,
};

export const getClientsList = createAsyncThunk<
  ClientUIResponse,
  {
    startDate: string;
    endDate: string;
    fdpId: string;
  }
>("clients/GetClientsList", async ({ startDate, endDate, fdpId }) => {
  const response = await agent.ClientAgent.getClientsList(
    startDate,
    endDate,
    fdpId
  );
  return response;
});

export const editClientInformation = createAsyncThunk<
  ApiResponse,
  UpdateClientRequest
>("clients/edit-client-data", async (clientData: UpdateClientRequest) => {
  const response = await agent.ClientAgent.editClientInformation(clientData);
  return response;
});

export const getDdClientsList = createAsyncThunk<ClientUIResponse>(
  "clients/GetDdClientsList",
  async () => {
    const response = await agent.ClientAgent.getDdClientsList();
    return response;
  }
);

export const downloadClientDataToCsv = createAsyncThunk<
  ClientDownloadResponse,
  Client[]
>("clients/downloadCsv", async (clientData: Client[]) => {
  const response = agent.ClientAgent.clientCsvDownload(clientData);
  return response;
});

export const downloadClientDataToXlsx = createAsyncThunk<Blob, Client[]>(
  "clients/downloadCsv",
  async (clientData: Client[]) => {
    const response = agent.ClientAgent.clientXlsxDownload(clientData);
    return response;
  }
);

export const addClient = createAsyncThunk<ApiResponse, AddClientRequest>(
  "clients/addClient",
  async (addClientRequest: AddClientRequest) => {
    const response = await agent.ClientAgent.addClient(addClientRequest);
    return response;
  }
);

export const addClientPcFdpMapping = createAsyncThunk<
  ClientUISingleResponse,
  any
>("clients/addClientPcFdpMapping", async (addClientRequest: any) => {
  const response = await agent.ClientAgent.addClientPcFdpMapping(
    addClientRequest
  );
  return response;
});

export const editClientPcFdpMapping = createAsyncThunk<
  ClientUISingleResponse,
  any
>("clients/editClientPcFdpMapping", async (editClientRequest: any) => {
  const response = await agent.ClientAgent.editClientPcFdpMapping(
    editClientRequest
  );
  return response;
});

export const updateClient = createAsyncThunk<ApiResponse, UpdateClientRequest>(
  "/clients/updateClient",
  async (updateClientRequest: UpdateClientRequest) => {
    const response = await agent.ClientAgent.updateClient(updateClientRequest);
    return response;
  }
);

export const deleteClient = createAsyncThunk<ApiResponse, UpdateClientRequest>(
  "/clients/deleteClient",
  async (deleteClientRequest: UpdateClientRequest) => {
    const response = await agent.ClientAgent.deleteClient(deleteClientRequest);
    return response;
  }
);
const clientSlice = createSlice({
  name: "client",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getClientsList.pending, (state) => {
        state.isClientLoading = true;
      })
      .addCase(getClientsList.fulfilled, (state, action) => {
        state.isClientLoading = false;
        if (action.payload.isSuccess) {
          state.clientList = action.payload.data;
        } else {
          state.clientList = [];
        }
      })
      .addCase(getClientsList.rejected, (state, action) => {
        state.isClientLoading = false;
        state.clientList = [];
        state.errorMessage = action.error.message!;
      })
      .addCase(getDdClientsList.pending, (state) => {
        state.isClientLoading = true;
      })
      .addCase(getDdClientsList.fulfilled, (state, action) => {
        state.isClientLoading = false;
        if (action.payload.isSuccess) {
          state.clientList = action.payload.data;
        } else {
          state.clientList = [];
        }
      })
      .addCase(getDdClientsList.rejected, (state, action) => {
        state.isClientLoading = false;
        state.clientList = [];
        state.errorMessage = action.error.message!;
      })
      .addCase(addClientPcFdpMapping.fulfilled, (state, action) => {
        state.isClientLoading = false;
        if (action.payload.isSuccess) {
          const data = action.payload.data;
          state.clientList = state.clientList.map((e, idx) => {
            if (e.id === data.id) {
              return data;
            }
            return e;
          });
        } else {
          state.clientList = [];
        }
      })
      .addCase(editClientPcFdpMapping.fulfilled, (state, action) => {
        state.isClientLoading = false;
        if (action.payload.isSuccess) {
          const data = action.payload.data;
          state.clientList = state.clientList.map((e, idx) => {
            if (e.id === data.id) {
              return data;
            }
            return e;
          });
        } else {
          state.clientList = [];
        }
      });
  },
});

export default clientSlice.reducer;
